// src/components/PayJobButton.js
// Handles the customer-side payment flow for a completed job.
// STATE A: job not Completed → renders nothing
// STATE B: Completed but not paid → shows Pay button → Stripe Elements inline form
// STATE C: paid → shows green confirmation badge
import React, { useState } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripe } from '../services/stripe';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../firebase';
import './PayJobButton.css';

const functions = getFunctions(app);

// ── Inner checkout form rendered once we have a clientSecret ─────────────────
const CheckoutForm = ({ amount, onSuccess, onError }) => {
  const [paid, setPaid] = useState(false);
  const stripe   = useStripe();
  const elements = useElements();
  const [confirming, setConfirming] = useState(false);
  const [formError, setFormError]   = useState('');

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setConfirming(true);
    setFormError('');
    // redirect: 'if_required' keeps card payments in-page; redirect-based methods still
    // go to /payment-success, which verifies the payment via markJobPaid.
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/payment-success`,
      },
      redirect: 'if_required',
    });
    if (error) {
      setFormError(error.message || 'Payment failed. Please try again.');
      onError && onError(error.message);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      setPaid(true);
      // Robustness fallback in case the webhook is delayed: verify server-side and mark paid
      try {
        const markJobPaid = httpsCallable(functions, 'markJobPaid');
        await markJobPaid({ paymentIntentId: paymentIntent.id });
      } catch (err) {
        console.warn('[PayJobButton] markJobPaid fallback failed (webhook will retry):', err?.message);
      }
      onSuccess && onSuccess(paymentIntent);
    } else if (paymentIntent) {
      setFormError(`Payment status: ${paymentIntent.status}. Please wait or try again.`);
    }
    setConfirming(false);
  };

  if (paid) {
    return <div className="pjb-paid-badge">✅ Payment complete — £{(amount / 100).toFixed(2)}</div>;
  }

  return (
    <form onSubmit={handleConfirm} className="pjb-checkout-form">
      <PaymentElement />
      {formError && <p className="pjb-error">{formError}</p>}
      <button
        type="submit"
        className="btn btn-primary pjb-confirm-btn"
        disabled={!stripe || confirming}
      >
        {confirming ? 'Processing…' : `Confirm Payment — £${(amount / 100).toFixed(2)}`}
      </button>
    </form>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const PayJobButton = ({ job, acceptedBid }) => {
  const [loading,      setLoading]      = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [amount,       setAmount]       = useState(null);
  const [stripeAcct,   setStripeAcct]   = useState(null);
  const [error,        setError]        = useState('');

  // Diagnostic logging
  console.log('[PayJobButton] job.status:', job?.status, '| paymentStatus:', job?.paymentStatus, '| jobId:', job?.id);

  // STATE A — job not yet completed
  if (!job || job.status !== 'Completed') {
    console.log('[PayJobButton] → STATE A (not completed), rendering null');
    return null;
  }

  // Free jobs (scrap collection or £0 bid) never show a Pay button
  const bidPrice = parseFloat(String(acceptedBid?.price ?? job.price ?? '').replace(/[^0-9.]/g, ''));
  if (job.serviceType === 'Scrap Collection' || bidPrice === 0) {
    return null;
  }

  // STATE C — already paid
  if (job.paymentStatus === 'paid') {
    console.log('[PayJobButton] → STATE C (paid)');
    return (
      <div className="pjb-paid-badge">
        ✅ Payment complete{amount ? ` — £${(amount / 100).toFixed(2)}` : ''}
      </div>
    );
  }

  // STATE B — completed, not yet paid
  console.log('[PayJobButton] → STATE B (completed, unpaid) — rendering Pay button');
  const handlePay = async () => {
    setLoading(true);
    setError('');
    try {
      const createPaymentIntent = httpsCallable(functions, 'createPaymentIntent');
      const result = await createPaymentIntent({ jobId: job.id });
      setStripeAcct(result.data.stripeAccount || null); // direct charge on carrier account
      setAmount(result.data.amount);
      setClientSecret(result.data.clientSecret);
    } catch (err) {
      console.error('[PayJobButton] createPaymentIntent error:', err);
      setError(err.message || 'Could not start payment. Please try again.');
    }
    setLoading(false);
  };

  const carrierName = acceptedBid?.providerName || 'Carrier';
  const displayPrice = acceptedBid?.price ? `£${acceptedBid.price}` : '';

  return (
    <div className="pjb-wrap">
      {!clientSecret ? (
        <>
          <button
            className="btn btn-primary pjb-pay-btn"
            onClick={handlePay}
            disabled={loading}
          >
            {loading ? 'Loading…' : `💳 Pay ${displayPrice} to ${carrierName}`}
          </button>
          {error && <p className="pjb-error">{error}</p>}
        </>
      ) : (
        <div className="pjb-elements-wrap">
          <div className="pjb-elements-header">
            💳 Pay <strong>£{(amount / 100).toFixed(2)}</strong> to {carrierName}
          </div>
          <Elements
            stripe={getStripe(stripeAcct)}
            options={{ clientSecret, appearance: { theme: 'night', variables: { colorPrimary: '#3ecfa0' } } }}
          >
            <CheckoutForm
              amount={amount}
              onError={msg => setError(msg)}
            />
          </Elements>
        </div>
      )}
    </div>
  );
};

export default PayJobButton;
